/**
 * Form 8233 Custom Hook
 * Centralized state management and business logic for Form 8233
 */

import { useState, useEffect, useCallback } from "react";
import { Form8233Model } from "../Models/Form8233Models";
import { Form8233Service } from "../services/Form8233Service";

interface UseForm8233Options {
  initialData?: any;
}

interface UseForm8233Result {
  model: Form8233Model;
  isLoading: boolean;
  isSaving: boolean;
  errors: Record<string, string>;
  isValid: boolean;
  updateField: (fieldName: string, value: any) => void;
  updateMultipleFields: (fields: Record<string, any>) => void;
  validate: () => boolean;
}

export const useForm8233 = (options: UseForm8233Options = {}): UseForm8233Result => {
  const { initialData } = options;

  // Core state
  const [model, setModel] = useState<Form8233Model>(() => {
    if (initialData) {
      return Form8233Service.fromApiResponse(initialData);
    }
    return new Form8233Model();
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isValid, setIsValid] = useState(false);

  // Validation effect
  useEffect(() => {
    const validation = model.validate();
    setErrors(validation.fieldErrors);
    setIsValid(validation.isValid);
  }, [model]);

  // Update a single field
  const updateField = useCallback((fieldName: string, value: any) => {
    setModel((prevModel) => {
      const newModel = new Form8233Model(prevModel);
      (newModel as any)[fieldName] = value;
      return newModel;
    });
  }, []);

  // Update multiple fields at once
  const updateMultipleFields = useCallback((fields: Record<string, any>) => {
    setModel((prevModel) => {
      const newModel = new Form8233Model(prevModel);
      Object.entries(fields).forEach(([fieldName, value]) => {
        (newModel as any)[fieldName] = value;
      });
      return newModel;
    });
  }, []);

  // Validate the current model
  const validate = useCallback((): boolean => {
    const validation = model.validate();
    setErrors(validation.fieldErrors);
    setIsValid(validation.isValid);
    return validation.isValid;
  }, [model]);

  return {
    model,
    isLoading,
    isSaving,
    errors,
    isValid,
    updateField,
    updateMultipleFields,
    validate,
  };
};
